class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      const target = event.target;
      if (!target) return;

      if (target.classList.contains('facets__add-filter')) {
        this.onAddFilterHandler(event);
      } else {
        this.onSubmitHandler(event);
        if (target.type === 'radio') {
          const filterDetails = target.closest('.facets__filters__details');
          const selectedSort = filterDetails?.querySelector('.facet-filters__selected-sort');
          if (selectedSort) selectedSort.textContent = target.dataset.sortName || '';
        }
      }
    }, 500);

    const form = this.querySelector('form');
    if (form) form.addEventListener('input', this.debouncedOnSubmit);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    if (FacetFiltersForm.historyListenerBound) return;
    const onHistoryChange = (event) => {
      const eventState = event.state || {};
      const params = eventState.searchParams || eventState.urlParams;
      const searchParams = params ?? FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    FacetFiltersForm.historyListenerBound = true;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoadingState(isLoading) {
    const mobileFacetsMain = document.querySelector('.mobile-facets__main');
    mobileFacetsMain?.classList.toggle('mobile-facets__main--loading', isLoading);

    if (!isLoading) {
      document.querySelectorAll('.mobile-facets__label--loading').forEach((element) => {
        const checkbox = element.querySelector('.mobile-facets__checkbox');
        element.classList.remove('mobile-facets__label--loading');
        element.classList.toggle('mobile-facets__label--checked', Boolean(checkbox?.checked));
      });
      document.querySelectorAll('.facets__selected--loading').forEach((element) => {
        element.classList.remove('facets__selected--loading');
      });
    }
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const requestId = ++FacetFiltersForm.requestSerial;
    FacetFiltersForm.activeController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.activeController = controller;
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.toggleActiveFacets(true);
    FacetFiltersForm.setLoadingState(true);

    const sections = FacetFiltersForm.getSections();
    if (!sections.length) {
      FacetFiltersForm.setLoadingState(false);
      FacetFiltersForm.toggleActiveFacets(false);
      return;
    }

    try {
      await Promise.all(sections.map(async (section) => {
        const suffix = searchParams ? `&${searchParams}` : '';
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(section.section)}${suffix}`;
        const filterDataUrl = (element) => element.url === url;
        const cached = FacetFiltersForm.filterData.find(filterDataUrl);

        if (cached) {
          if (requestId !== FacetFiltersForm.requestSerial) return;
          FacetFiltersForm.renderSectionFromCache(cached, event);
          return;
        }

        await FacetFiltersForm.renderSectionFromFetch(url, event, controller.signal, requestId);
      }));

      if (requestId !== FacetFiltersForm.requestSerial) return;
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Unable to update storefront filters.', error);
      }
    } finally {
      if (requestId === FacetFiltersForm.requestSerial) {
        FacetFiltersForm.setLoadingState(false);
        FacetFiltersForm.toggleActiveFacets(false);
        if (FacetFiltersForm.activeController === controller) FacetFiltersForm.activeController = null;
      }
    }
  }

  static async fetchMarkup(url, signal) {
    const response = await fetch(url, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Filter request failed with ${response.status}`);
    const text = await response.text();
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    if (!parsed.getElementById('data-pagination') && !parsed.getElementById('ProductGridContainer')) {
      throw new Error('Filter response did not contain the expected product grid markup.');
    }
    return { text, parsed };
  }

  static async renderSectionFromFetch(url, event, signal, requestId) {
    const params = getAllUrlParams(url);
    const page = params.page ? Number.parseInt(params.page, 10) : null;
    let html;

    if (page && page > 1) {
      const pages = Array.from({ length: page }, (_, index) => index + 1);
      const results = await Promise.all(pages.map((pageNumber) => {
        const fetchUrl = url.replace(/page=[0-9]+/, `page=${pageNumber}`);
        return FacetFiltersForm.fetchMarkup(fetchUrl, signal);
      }));

      if (requestId !== FacetFiltersForm.requestSerial) return;

      const combined = document.createElement('div');
      results.forEach(({ parsed }) => {
        const pagination = parsed.getElementById('data-pagination');
        if (pagination && !pagination.dataset.empty) {
          combined.insertAdjacentHTML('beforeend', pagination.innerHTML);
        }
      });

      const finalDocument = results[results.length - 1]?.parsed;
      const finalPagination = finalDocument?.getElementById('data-pagination');
      const body = finalDocument?.querySelector('body');
      if (!finalPagination || !body) throw new Error('Filter pagination response was incomplete.');
      finalPagination.innerHTML = combined.innerHTML;
      html = body.outerHTML;
    } else {
      const result = await FacetFiltersForm.fetchMarkup(url, signal);
      html = result.text;
    }

    if (requestId !== FacetFiltersForm.requestSerial) return;

    FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }].slice(-30);
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderPagination(html);
  }

  static renderSectionFromCache(cached, event) {
    if (!cached?.html) return;
    FacetFiltersForm.renderFilters(cached.html, event);
    FacetFiltersForm.renderProductGridContainer(cached.html);
    FacetFiltersForm.renderPagination(cached.html);
  }

  static renderProductGridContainer(html) {
    const htmlElm = new DOMParser().parseFromString(html, 'text/html');
    const nextUrlElm = document.querySelector('[data-next-url]');
    const sourceNextUrl = htmlElm.querySelector('[data-next-url]');
    const dataPagination = document.getElementById('data-pagination');
    const sourceDataPagination = htmlElm.getElementById('data-pagination');
    const productGrid = document.getElementById('ProductGridContainer');
    const sourceProductGrid = htmlElm.getElementById('ProductGridContainer');

    if (dataPagination && sourceDataPagination) {
      dataPagination.innerHTML = sourceDataPagination.innerHTML;
    } else if (productGrid && sourceProductGrid) {
      productGrid.innerHTML = sourceProductGrid.innerHTML;
    } else {
      throw new Error('Unable to locate the product grid target for filter results.');
    }

    if (nextUrlElm) nextUrlElm.value = sourceNextUrl?.value || '';

    if (FacetFiltersForm.triggerAddFilter) {
      FacetFiltersForm.triggerAddFilter = false;
      if (FacetFiltersForm.handleFilter) {
        FacetFiltersForm.handleFilter.classList.toggle('hidden', !FacetFiltersForm.showFilter);
      }
    }
  }

  static renderPagination(html) {
    const htmlElm = new DOMParser().parseFromString(html, 'text/html');
    const componentPaginationLoader = document.getElementById('component-pagination-loader');
    const sourceComponentPaginationLoader = htmlElm.getElementById('component-pagination-loader');
    if (!componentPaginationLoader) return;

    if (!sourceComponentPaginationLoader) {
      componentPaginationLoader.innerHTML = '';
      return;
    }

    const paginationLoader = componentPaginationLoader.querySelector('pagination-loader');
    const sourcePaginationLoader = sourceComponentPaginationLoader.querySelector('pagination-loader');

    if (paginationLoader && sourcePaginationLoader) {
      const nextUrlElm = paginationLoader.querySelector('input[data-next-url]');
      const totalPagesElm = paginationLoader.querySelector('input[data-total-pages]');
      const currentPageElm = paginationLoader.querySelector('input[data-current-page]');
      const loadMoreButton = paginationLoader.querySelector('.js-load-more');
      const sourceNextUrl = sourcePaginationLoader.querySelector('input[data-next-url]');
      const sourceTotalPages = sourcePaginationLoader.querySelector('input[data-total-pages]');
      const sourceCurrentPage = sourcePaginationLoader.querySelector('input[data-current-page]');
      if (!nextUrlElm || !totalPagesElm || !currentPageElm || !loadMoreButton || !sourceTotalPages || !sourceCurrentPage) return;

      const currentPage = Number.parseInt(sourceCurrentPage.value, 10) || 1;
      const totalPages = Number.parseInt(sourceTotalPages.value, 10) || 1;
      nextUrlElm.value = sourceNextUrl?.value || '';
      totalPagesElm.value = String(totalPages);
      currentPageElm.value = String(currentPage);
      loadMoreButton.classList.toggle('hide', currentPage >= totalPages || !sourceNextUrl?.value);
    } else {
      if (sourcePaginationLoader) sourcePaginationLoader.dataset.disableLoadPages = 'true';
      componentPaginationLoader.innerHTML = sourceComponentPaginationLoader.innerHTML;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event?.target?.closest?.('.js-filter');
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${CSS.escape(element.dataset.index || '')}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    const target = event?.target?.closest?.('.js-filter') || null;
    FacetFiltersForm.renderActiveFacets(countsToRender, target, parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.renderAddFilters(parsedHTML);

    if (countsToRender && target) {
      FacetFiltersForm.renderCounts(countsToRender, target);
      FacetFiltersForm.renderMobileCounts(countsToRender, target);
    }
  }

  static renderActiveFacets(source, target, html) {
    const targetElement = target?.querySelector('.active-facets');
    const sourceElement = source?.querySelector('.active-facets');
    const activeFacetsElement = html.querySelector('.active-facets-mobile');
    if (targetElement && sourceElement) targetElement.innerHTML = sourceElement.innerHTML;

    const activeFacetsTarget = document.querySelector('.active-facets-mobile');
    if (activeFacetsElement && activeFacetsTarget) activeFacetsTarget.innerHTML = activeFacetsElement.innerHTML;
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    const clearFilter = '.facet-filters__clear-filters';

    html.querySelectorAll(clearFilter).forEach((element) => {
      const dataIndex = element.dataset.clearFilter;
      const target = document.querySelector(`${clearFilter}[data-clear-filter="${CSS.escape(dataIndex || '')}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm?.closest('menu-drawer');
    if (typeof menuDrawer?.bindEvents === 'function') menuDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const pairs = [
      ['.facets__selected', '.facets__selected'],
      ['.facets__summary', '.facets__summary'],
      ['.facets__header', '.facets__header'],
      ['.facets-wrap', '.facets-wrap'],
    ];
    pairs.forEach(([targetSelector, sourceSelector]) => {
      const targetElement = target?.querySelector(targetSelector);
      const sourceElement = source?.querySelector(sourceSelector);
      if (sourceElement && targetElement) targetElement.outerHTML = sourceElement.outerHTML;
    });
  }

  static renderMobileCounts(source, target) {
    const pairs = [
      ['.mobile-facets__summary', '.mobile-facets__summary'],
      ['.mobile-facets__list', '.mobile-facets__list'],
    ];
    pairs.forEach(([targetSelector, sourceSelector]) => {
      const targetElement = target?.querySelector(targetSelector);
      const sourceElement = source?.querySelector(sourceSelector);
      if (sourceElement && targetElement) targetElement.outerHTML = sourceElement.outerHTML;
    });
  }

  static renderAddFilters(parsedHTML) {
    parsedHTML.querySelectorAll('.facets__add-filter').forEach((element) => {
      const target = document.querySelector(`.facets__add-filter[data-index="${CSS.escape(element.dataset.index || '')}"]`);
      if (target) target.dataset.url = element.dataset.url || '';
    });
  }

  static updateURLHash(searchParams) {
    const nextUrl = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    history.pushState({ searchParams }, '', nextUrl);
  }

  static getSections() {
    const pagination = document.getElementById('data-pagination');
    const section = pagination?.dataset.id;
    return section ? [{ section }] : [];
  }

  createSearchParams(form) {
    return form ? new URLSearchParams(new FormData(form)).toString() : '';
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const target = event.target;
    const targetForm = target?.closest?.('form');
    if (!target || !targetForm) return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (target.classList.contains('mobile-facets__checkbox')) {
      const mobileFacetItem = target.closest('.mobile-facets__label');
      mobileFacetItem?.classList.add('mobile-facets__label--loading');
      this.onSubmitForm(this.createSearchParams(targetForm), event);
      return;
    }

    const forms = [];
    const isMobile = targetForm.id === 'FacetFiltersFormMobile';
    sortFilterForms.forEach((form) => {
      if (!isMobile && (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm')) {
        forms.push(this.createSearchParams(form));
      } else if (isMobile && form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });

    const facetsSelected = target.closest('.facets-wrap')?.querySelector('.facets__selected');
    facetsSelected?.classList.add('facets__selected--loading');
    this.onSubmitForm(forms.join('&'), event);
  }

  onAddFilterHandler(event) {
    event.preventDefault();
    const target = event.target;
    const form = target?.closest?.('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!target || !form) return;

    FacetFiltersForm.triggerAddFilter = true;
    FacetFiltersForm.handleFilter = form.querySelector(`#Wrap-Filter-${CSS.escape(target.value || '')}-${CSS.escape(target.dataset.index || '')}`);
    FacetFiltersForm.showFilter = target.checked;
    form.onActiveFilterClick(event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets(true);

    const href = event.currentTarget?.href || event.target?.dataset?.url;
    if (typeof href !== 'string') {
      FacetFiltersForm.toggleActiveFacets(false);
      return;
    }
    const queryIndex = href.indexOf('?');
    const searchParams = queryIndex === -1 ? '' : href.slice(queryIndex + 1);
    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.requestSerial = 0;
FacetFiltersForm.activeController = null;
FacetFiltersForm.historyListenerBound = false;
if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', '0');
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max') || '');
  }

  adjustToValidValues(input) {
    if (!input) return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (Number.isNaN(value)) return;
    if (!Number.isNaN(min) && value < min) input.value = min;
    if (!Number.isNaN(max) && value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.closeFilter(event);
      }
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') form.onActiveFilterClick(event);
  }
}

if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);
